import React from 'react';
import { ButtonGroup, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { TOOLS } from './useFloorPlanTools';

function FloorPlanToolbar({
  activeTool,
  setActiveTool,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  settings,
  onSettingsChange,
  onExport,
  drawingWall,
  onCancelDrawing,
}) {
  const toolBtn = (tool, icon, label) => (
    <OverlayTrigger placement="bottom" overlay={<Tooltip>{label}</Tooltip>}>
      <Button
        variant={activeTool === tool ? 'secondary' : 'outline-secondary'}
        size="sm"
        onClick={() => setActiveTool(tool)}
        className="floor-plan-tool-btn"
      >
        <i className={`bi ${icon}`}></i>
      </Button>
    </OverlayTrigger>
  );

  const actionBtn = (onClick, icon, label, disabled = false) => (
    <OverlayTrigger placement="bottom" overlay={<Tooltip>{label}</Tooltip>}>
      <span>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onClick}
          disabled={disabled}
          className="floor-plan-tool-btn"
        >
          <i className={`bi ${icon}`}></i>
        </Button>
      </span>
    </OverlayTrigger>
  );

  const toggleBtn = (active, onClick, icon, label) => (
    <OverlayTrigger placement="bottom" overlay={<Tooltip>{label}</Tooltip>}>
      <Button
        variant={active ? 'secondary' : 'outline-secondary'}
        size="sm"
        onClick={onClick}
        className="floor-plan-tool-btn"
      >
        <i className={`bi ${icon}`}></i>
      </Button>
    </OverlayTrigger>
  );

  return (
    <div className="floor-plan-toolbar d-flex align-items-center gap-2 p-2 border-bottom bg-white flex-wrap">
      {/* Drawing tools */}
      <ButtonGroup size="sm">
        {toolBtn(TOOLS.SELECT, 'bi-cursor', 'Select (Esc)')}
        {toolBtn(TOOLS.WALL, 'bi-border-outer', 'Draw Wall')}
        {toolBtn(TOOLS.DOOR, 'bi-door-open', 'Place Door')}
        {toolBtn(TOOLS.WINDOW, 'bi-window-stack', 'Place Window')}
        {toolBtn(TOOLS.FURNITURE, 'bi-lamp', 'Add Furniture')}
      </ButtonGroup>

      {/* Drawing status */}
      {drawingWall && (
        <span className="text-muted small d-flex align-items-center gap-1">
          <span>Drawing wall...</span>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={onCancelDrawing}
            className="py-0 px-1"
            style={{ fontSize: '0.7rem' }}
          >
            Cancel
          </Button>
        </span>
      )}

      <div className="vr mx-1" />

      {/* Undo/Redo */}
      <ButtonGroup size="sm">
        {actionBtn(onUndo, 'bi-arrow-counterclockwise', 'Undo (Ctrl+Z)', !canUndo)}
        {actionBtn(onRedo, 'bi-arrow-clockwise', 'Redo (Ctrl+Shift+Z)', !canRedo)}
      </ButtonGroup>

      <div className="vr mx-1" />

      {/* View toggles */}
      <ButtonGroup size="sm">
        {toggleBtn(settings.showGrid, () => onSettingsChange({ showGrid: !settings.showGrid }), 'bi-grid-3x3', 'Toggle Grid')}
        {toggleBtn(settings.snapToGrid, () => onSettingsChange({ snapToGrid: !settings.snapToGrid }), 'bi-magnet', 'Snap to Grid')}
        {toggleBtn(settings.showDimensions, () => onSettingsChange({ showDimensions: !settings.showDimensions }), 'bi-rulers', 'Show Dimensions')}
      </ButtonGroup>

      {/* Spacer */}
      <div className="flex-grow-1" />

      {/* Unit toggle */}
      <ButtonGroup size="sm">
        <Button
          variant={settings.unit === 'imperial' ? 'secondary' : 'outline-secondary'}
          size="sm"
          onClick={() => onSettingsChange({ unit: 'imperial' })}
          className="floor-plan-tool-btn"
        >
          ft
        </Button>
        <Button
          variant={settings.unit === 'metric' ? 'secondary' : 'outline-secondary'}
          size="sm"
          onClick={() => onSettingsChange({ unit: 'metric' })}
          className="floor-plan-tool-btn"
        >
          m
        </Button>
      </ButtonGroup>

      {/* Export */}
      {actionBtn(onExport, 'bi-download', 'Export as PNG')}
    </div>
  );
}

export default React.memo(FloorPlanToolbar);
